import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { formatCurrency } from "../../utils"; // Assuming you have this utility
import JsBarcode from 'jsbarcode';

// Convert mm to points (1mm = 2.834645669 points)
const mmToPoints = (mm) => mm * 2.834645669;

// Label dimensions: 51mm x 25mm
const LABEL_WIDTH = mmToPoints(51);
const LABEL_HEIGHT = mmToPoints(25);

// PDF Styles
const styles = StyleSheet.create({
    page: {
        width: LABEL_WIDTH,
        height: LABEL_HEIGHT,
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        // justifyContent: 'flex-end', // Position content at bottom
    },
    productName: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#000000',
        justifyContent: 'center',
        paddingHorizontal: mmToPoints(2),
    },
    bottomSection: {
        flexDirection: 'row',
        height: mmToPoints(15),
        paddingRight: mmToPoints(2),
        width: '100%',
    },
    barcodeContainer: {
        width: '77%',
        height: '100%',
        justifyContent: 'flex-start',
        flexDirection: 'column',
    },
    priceContainer: {
        width: '23%',
        height: mmToPoints(9),
        flexDirection: 'column',
        paddingTop: mmToPoints(1),
        justifyContent: 'center',
        alignItems: 'center',
    },
    originalPrice: {
        fontSize: 7,
        color: '#666666',
        textAlign: 'center',
        marginBottom: 1,
        position: 'relative',
    },
    strikeOverlay: {
        position: 'absolute',
        top: '50%',
        left: 0,
        right: 0,
        height: 0.5,
        backgroundColor: '#666666',
        transform: 'rotate(14deg)',
    },
    currentPrice: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#000000',
        textAlign: 'center',
    },
    singlePrice: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#000000',
        textAlign: 'center',
    },
    barcodeText: {
        fontSize: 7,
        color: '#000000',
        textAlign: 'center',
    },
});

const generateBarcodeBase64 = (value) => {
    const canvas = document.createElement('canvas');
    
    JsBarcode(canvas, value, {
        format: "CODE128", // Of "EAN13", "UPC", etc.
        width: 2,
        height: 50,
        displayValue: false, // Geen tekst in de barcode zelf
        margin: 10,
        background: "#ffffff",
        lineColor: "#000000"
    });
    
    return canvas.toDataURL('image/png');
};

/**
 * Truncate text to fit label with word wrapping
 */
const truncateText = (text, maxLength) => {
    if (!text) return '';
    
    // If text is short enough, return as is
    if (text.length <= maxLength) return text;
    
    // Otherwise truncate normally
    return text.substring(0, maxLength - 3) + '...';
};

/**
 * Single Label Page Component
 */
const LabelPage = ({ product, template }) => {
    const hasDiscount = product.salePrice && product.price > product.salePrice;
    const barcodeValue = product.barcode;
    
    // Generate barcode image
    const barcodeImageBase64 = generateBarcodeBase64(barcodeValue);
  
    // Barcode component
    const barcodeComponent = (
        <View style={styles.barcodeContainer}>
            <Image 
                src={barcodeImageBase64} 
                style={{ width: '100%', height: mmToPoints(10) }}
            />
            <Text style={styles.barcodeText}>{barcodeValue}</Text>
        </View>
    );
    
    // Price component
    const priceComponent = (
        <View style={styles.priceContainer}>
            {hasDiscount ? (
                <>
                    <View style={{ position: 'relative' }}>
                        <Text style={styles.originalPrice}>
                            {formatCurrency(product.price)}
                        </Text>
                        <View style={styles.strikeOverlay} />
                    </View>
                    <Text style={styles.currentPrice}>
                        {formatCurrency(product.salePrice)}
                    </Text>
                </>
            ) : (
                <Text style={styles.singlePrice}>
                    {formatCurrency(product.price)}
                </Text>
            )}
        </View>
    );
    
    return (
        <Page size={[LABEL_WIDTH, LABEL_HEIGHT]} style={styles.page}>
            {/* Product Name at the top of the bottom section */}
            <View style={[styles.productName, { paddingTop: mmToPoints(1) }]}>
                <Text>
                    {truncateText(product.product_name, 80)}
                </Text>
            </View>

            <View style={{ flex: 1, justifyContent: 'flex-end' }}>
                <View style={[styles.productName, { flexDirection: 'row', justifyContent: 'space-evenly' }]}>
                    {product.color && (
                        <Text>
                            Kleur: {product.color}
                        </Text>
                    )}
                    {product.size && (
                        <Text>
                            Maat: {product.size}
                        </Text>
                    )}
                </View>
                
                {/* Bottom section with conditional order based on template */}
                <View style={styles.bottomSection}>
                    {template === 'price_right' ? (
                        <>
                            {/* Barcode left, Price right */}
                            {barcodeComponent}
                            {priceComponent}
                        </>
                    ) : (
                        <>
                            {/* Price left, Barcode right */}
                            {priceComponent}
                            {barcodeComponent}
                        </>
                    )}
                </View>
            </View>
        </Page>
    );
};

/**
 * Main PDF Document Component
 * Each product gets its own page with label dimensions
 */
const LabelPDFDocument = ({ products, template }) => {
    if (!products || products.length === 0) {
        return (
            <Document>
                <Page size={[LABEL_WIDTH, LABEL_HEIGHT]} style={[styles.page, { justifyContent: 'center' }]}>
                    <Text style={{ fontSize: 8, textAlign: 'center' }}>
                        Er zijn geen geldige producten geselecteerd om een label te maken.
                    </Text>
                </Page>
            </Document>
        );
    }
    
    return (
        <Document>
            {products.map((product, index) => (
                <LabelPage key={index} product={product} template={template} />
            ))}
        </Document>
    );
};

export default LabelPDFDocument;